import { useState, useEffect, useCallback } from 'react';

interface UseSidebarReturn {
  isOpen: boolean;
  isCollapsed: boolean;
  isMobile: boolean;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleCollapse: () => void;
}

export function useSidebar(): UseSidebarReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem('sidebarCollapsed');
    return stored === 'true';
  });
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      // Auto-close sidebar on mobile
      if (mobile) {
        setIsOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Toggle sidebar
  // On desktop: toggles collapsed state (expand/collapse)
  // On mobile: toggles open state (show/hide overlay)
  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      // Mobile: toggle open/closed
      setIsOpen(prev => !prev);
    } else {
      // Desktop: toggle collapsed/expanded
      setIsCollapsed(prev => {
        const newValue = !prev;
        localStorage.setItem('sidebarCollapsed', String(newValue));
        return newValue;
      });
    }
  }, [isMobile]);

  // Open sidebar
  const openSidebar = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close sidebar
  const closeSidebar = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Toggle collapsed state (for desktop mini mode)
  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('sidebarCollapsed', String(newValue));

      // Toggle body class for global styling
      if (newValue) {
        document.body.classList.add('sidebar-mini');
      } else {
        document.body.classList.remove('sidebar-mini');
      }

      return newValue;
    });
  }, []);

  // Apply initial collapsed state
  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add('sidebar-mini');
    }
    return () => {
      document.body.classList.remove('sidebar-mini');
    };
  }, [isCollapsed]);

  return {
    isOpen,
    isCollapsed,
    isMobile,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    toggleCollapse,
  };
}
